import { useMemo } from 'react';

import { Link as MuiLink, Typography } from '@mui/material';
import { createLink, useNavigate, useSearch } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';

import createMediaPlanColumn from './createMediaPlanColumn';
import createSelectColumn from './createSelectColumn';
import AlgorithmCell from '../components/cells/AlgorithmCell';
import AlgorithmLevelCell from '../components/cells/AlgorithmLevelCell';
import AlgorithmStatusCell from '../components/cells/AlgorithmStatusCell';
import AlgorithmLevelFilter from '../components/filters/AlgorithmLevelFilter';
import AlgorithmsFilter from '../components/filters/AlgorithmsFilter';
import AlgorithmStatusFilter from '../components/filters/AlgorithmStatusFilter';
import DateFilter from '../components/filters/DateFilter';
import MinMaxFilter from '../components/filters/MinMaxFilter';
import { ALGORITHM_LEVEL } from '../schemas';
import { TraffickingCampaignData } from '../types';

import useMediaPlanId from '@/features/mediaPlan/hooks/useMediaPlanId';
import {
  isAdvertiserBasedHierarchy,
  PLATFORM_SUPPORTS_MEDIA_PLANS,
  usePlatform,
} from '@/features/users';
import { formatCurrencyAsIs } from '@/shared/utils/utils';

const columnHelper = createColumnHelper<TraffickingCampaignData>();
const Link = createLink(MuiLink);

interface UseCampaignColumnsOptions {
  withDiagnosticsAction?: boolean;
  hideAlgorithmColumns?: boolean;
  disableCampaignNameLink?: boolean;
}

const useCampaignColumns = ({
  withDiagnosticsAction = false,
  hideAlgorithmColumns = false,
  disableCampaignNameLink = false,
}: UseCampaignColumnsOptions = {}) => {
  const platform = usePlatform();
  const isAdvertiserBased = isAdvertiserBasedHierarchy(platform);
  const includeMediaPlanColumn = PLATFORM_SUPPORTS_MEDIA_PLANS[platform];
  const mediaPlanIdParam = useMediaPlanId();

  const columns = useMemo(
    () => [
      ...(hideAlgorithmColumns ? [] : [createSelectColumn(columnHelper)]),
      columnHelper.accessor('campaignName', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Campaign Name
          </Typography>
        ),
        cell: (props) =>
          disableCampaignNameLink ? (
            <Typography
              variant='body2'
              component='span'
              color='text.tertiary'
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              {props.getValue()}
            </Typography>
          ) : (
            <Link
              to='/trafficking/ad-unit'
              search={{ search: props.row.original.campaignId }}
              underline='hover'
              color='primary.dark'
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              {props.getValue()}
            </Link>
          ),
        size: 280,
      }),
      columnHelper.accessor('campaignId', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Campaign ID
          </Typography>
        ),
        cell: (props) => (
          <Typography
            variant='subtitleNumbers'
            component='span'
            color='text.tertiary'
          >
            {props.getValue()}
          </Typography>
        ),
        size: isAdvertiserBased ? 160 : 220,
      }),
      columnHelper.accessor('budget', {
        enableSorting: false,
        header: () => (
          <Typography variant='body3' component='span'>
            Budget
          </Typography>
        ),
        cell: (props) => {
          const budget = props.getValue();

          if (budget === null) {
            return (
              <Typography
                variant='subtitleNumbers'
                component='span'
                color='text.secondary'
              >
                None
              </Typography>
            );
          }

          return (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {formatCurrencyAsIs(budget)}
            </Typography>
          );
        },
        size: 140,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <MinMaxFilter
                filterId='budgetFilter'
                buttonAriaLabel='budget filter'
                minPlaceholder='Min Budget'
                maxPlaceholder='Max Budget'
                searchParamMinValue={searchParams.budgetMin}
                searchParamMaxValue={searchParams.budgetMax}
                handleNavigation={(minValue, maxValue) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      budgetMin: minValue,
                      budgetMax: maxValue,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.accessor('startDate', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            Start Date
          </Typography>
        ),
        cell: (props) =>
          props.getValue() ? (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {new Date(props.getValue()).toLocaleDateString()}
            </Typography>
          ) : (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.secondary'
            >
              None
            </Typography>
          ),
        size: 150,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <DateFilter
                filterId='startDateFilter'
                buttonAriaLabel='start date filter'
                searchParamStringDate={searchParams.startDate}
                handleNavigation={(newDate) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      startDate: newDate,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      columnHelper.accessor('endDate', {
        enableSorting: true,
        header: () => (
          <Typography variant='body3' component='span'>
            End Date
          </Typography>
        ),
        cell: (props) =>
          props.getValue() ? (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.tertiary'
            >
              {new Date(props.getValue()).toLocaleDateString()}
            </Typography>
          ) : (
            <Typography
              variant='subtitleNumbers'
              component='span'
              color='text.secondary'
            >
              None
            </Typography>
          ),
        size: 150,
        meta: {
          FilterComponentFn: (_column) => {
            const searchParams = useSearch({ strict: false });
            const navigate = useNavigate();

            return (
              <DateFilter
                filterId='endDateFilter'
                buttonAriaLabel='end date filter'
                searchParamStringDate={searchParams.endDate}
                handleNavigation={(newDate) => {
                  void navigate({
                    to: '.',
                    search: {
                      ...searchParams,
                      endDate: newDate,
                      page: 1,
                    },
                    resetScroll: false,
                  });
                }}
              />
            );
          },
        },
      }),
      ...(hideAlgorithmColumns
        ? []
        : [
            columnHelper.accessor('algorithms', {
              enableSorting: true,
              sortDescFirst: false,
              header: () => (
                <Typography variant='body3' component='span'>
                  Algorithms
                </Typography>
              ),
              cell: (props) => (
                <AlgorithmCell
                  algorithmsStatus={props.row.original.algorithms}
                />
              ),
              size: 180,
              meta: {
                FilterComponentFn: (_column) => {
                  const searchParams = useSearch({ strict: false });
                  const navigate = useNavigate();

                  return (
                    <AlgorithmsFilter
                      filterId='algorithmsFilter'
                      buttonAriaLabel='algorithms filter'
                      searchParamAlgorithmIds={searchParams.algorithmId}
                      handleNavigation={(newAlgos) => {
                        void navigate({
                          to: '.',
                          search: {
                            ...searchParams,
                            algorithmId: newAlgos,
                            page: 1,
                          },
                          resetScroll: false,
                        });
                      }}
                    />
                  );
                },
              },
            }),
            columnHelper.accessor('algorithmStatus', {
              enableSorting: true,
              sortDescFirst: false,
              header: () => (
                <Typography variant='body3' component='span'>
                  Status
                </Typography>
              ),
              cell: (props) => (
                <AlgorithmStatusCell
                  algorithmsStatus={props.row.original.algorithms}
                />
              ),
              size: 200,
              meta: {
                FilterComponentFn: (_column) => {
                  const searchParams = useSearch({ strict: false });
                  const navigate = useNavigate();

                  return (
                    <AlgorithmStatusFilter
                      filterId='algorithmStatusFilter'
                      buttonAriaLabel='algorithm status filter'
                      searchParamAlgorithmStatuses={searchParams.status}
                      handleNavigation={(selectedAlgoStatuses) => {
                        void navigate({
                          to: '.',
                          search: {
                            ...searchParams,
                            status: selectedAlgoStatuses,
                            page: 1,
                          },
                          resetScroll: false,
                        });
                      }}
                    />
                  );
                },
              },
            }),
            columnHelper.display({
              id: 'algorithmLevel',
              enableSorting: false,
              header: () => (
                <Typography variant='body3' component='span'>
                  Level
                </Typography>
              ),
              cell: (props) => (
                <AlgorithmLevelCell
                  algorithmsStatus={props.row.original.algorithms}
                />
              ),
              size: 180,
              meta: {
                FilterComponentFn: (_column) => {
                  const searchParams = useSearch({ strict: false });
                  const navigate = useNavigate();

                  return (
                    <AlgorithmLevelFilter
                      level={ALGORITHM_LEVEL.CAMPAIGN}
                      filterId='algorithmLevelFilter'
                      buttonAriaLabel='algorithm level filter'
                      searchParamAlgorithmLevels={searchParams.level}
                      handleNavigation={(selectedAlgoLevels) => {
                        void navigate({
                          to: '.',
                          search: {
                            ...searchParams,
                            level: selectedAlgoLevels,
                            page: 1,
                          },
                          resetScroll: false,
                        });
                      }}
                    />
                  );
                },
              },
            }),
            ...(includeMediaPlanColumn
              ? [createMediaPlanColumn(columnHelper)]
              : []),
          ]),
      ...(withDiagnosticsAction && mediaPlanIdParam !== undefined
        ? [
            columnHelper.display({
              id: 'action',
              header: () => (
                <Typography variant='body3' component='span'>
                  Action
                </Typography>
              ),
              cell: (props) => (
                <Link
                  to='/media-plans/$id'
                  params={{ id: mediaPlanIdParam }}
                  search={(prev) => ({
                    ...prev,
                    tab: 'activation',
                    act: 'diagnostics',
                    diag: 'detail',
                    campaignId: props.row.original.campaignId,
                    adGroups: undefined,
                  })}
                  underline='hover'
                  color='primary.dark'
                >
                  Diagnostics
                </Link>
              ),
              size: 140,
            }),
          ]
        : []),
    ],
    [
      isAdvertiserBased,
      includeMediaPlanColumn,
      withDiagnosticsAction,
      mediaPlanIdParam,
      hideAlgorithmColumns,
      disableCampaignNameLink,
    ]
  );

  return columns;
};

export default useCampaignColumns;
